import { Block, type Change, Data, type Value } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import {
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_UL,
    type BlockOrderedListData,
    LIST_BLOCKS,
} from '../types';
import { getCurrentItem, getItemDepth, getItemsAtRange, getListForItem, isList } from './utils';

/**
 * Decreases the depth of the selected list items.
 *
 * No-op for root items.
 */
export function decreaseItemsDepth(change: Change, items?: List<Block>): Change {
    const { value } = change;
    const { document } = value;

    const itemsToMove = items ? items : getItemsAtRange(value);

    if (itemsToMove.size === 0) {
        return change;
    }

    // Support if a complete list is selected
    for (const item of itemsToMove) {
        if (LIST_BLOCKS.includes(item.type)) {
            change = decreaseItemsDepth(change, item.blockNodes);
        }
    }

    const firstItem = itemsToMove.first() as Block;
    const lastItem = itemsToMove.last() as Block;

    const depth = getItemDepth(value, firstItem);
    // Cannot decrease item depth of root items check if we can move the sublists of selected
    // top-level items
    if (depth === 1) {
        itemsToMove.forEach((item) => {
            const itemSubList = item.nodes.find((child) => isList(child)) as Block;
            if (!itemSubList || itemSubList.nodes.size === 0) {
                return true;
            }
            change = decreaseItemsDepth(change, itemSubList.nodes as List<Block>);
        });
    }

    const parentList = getListForItem(value, firstItem);
    if (!parentList) {
        return change;
    }
    const parentItem = document.getParent(parentList.key) as Block | void;
    if (!parentItem) {
        return change;
    }
    const parentItemParentList = getListForItem(value, parentItem);
    if (!parentItemParentList) {
        return change;
    }

    // The items following the last selected item will be moved to a sublist of item
    const followingItems = parentList.nodes.skipUntil((i) => i === lastItem).rest() as List<Block>;
    // True if the selected items and the followingItems make the whole
    // parentList, and hence the parentList will be emptied
    const willEmptyCurrentList = parentList.nodes.size === followingItems.size + itemsToMove.size;

    const parentItemIndex = parentItemParentList.getChildIndex(parentItem.key) + 1;
    itemsToMove.forEach((item, index) => {
        change = decreaseSingleItemDepth(
            change,
            item,
            parentList,
            parentItem,
            parentItemIndex + index,
            item === lastItem ? followingItems : undefined
        );
    });

    // Remove the currentList completely if needed
    if (willEmptyCurrentList) {
        change.removeNodeByKey(parentList.key);
    }

    return change.normalize();
}

function decreaseSingleItemDepth(
    change: Change,
    item: Block,
    parentList: Block,
    parentItem: Block,
    moveToIndex: number,
    followingItems?: List<Block>
): Change {
    const { value } = change;

    const parentItemParentList = getListForItem(value, parentItem);
    if (!parentItemParentList) {
        return change;
    }

    if (followingItems && !followingItems.isEmpty()) {
        // Add them as sublist of item
        const sublist = Block.create({
            object: 'block',
            type: parentList.type,
            data: parentList.data,
        });

        // Add the sublist
        change.insertNodeByKey(item.key, item.nodes.size, sublist, {
            normalize: false,
        });

        change.moveNodeByKey(item.key, parentItemParentList.key, moveToIndex, {
            normalize: false,
        });

        // Move the followingItems to the sublist
        followingItems.forEach((item, index) =>
            change.moveNodeByKey(item.key, sublist.key, sublist.nodes.size + index, {
                normalize: false,
            })
        );
    } else {
        change.moveNodeByKey(item.key, parentItemParentList.key, moveToIndex);
    }

    return change;
}

/**
 * Increase the depth of the selected list items.
 */
export function increaseItemsDepth(change: Change, items?: List<Block>): Change {
    const { value } = change;
    const { document, selection } = value;
    const selectedItems = items ? items : getItemsAtRange(value, selection);

    if (selectedItems.size === 0) {
        return change;
    }

    const firstSelectedItem = selectedItems.first();
    if (!firstSelectedItem) {
        return change;
    }

    // Support if a complete list is selected
    for (const item of selectedItems) {
        if (LIST_BLOCKS.includes(item.type)) {
            change = increaseItemsDepth(change, item.blockNodes);
        }
    }

    const parentListBlock = getListForItem(value, firstSelectedItem);
    if (!parentListBlock) {
        return change;
    }

    let previousItem = document.getPreviousSibling(firstSelectedItem.key);
    // No items above the first in selection, use it to nest other items under
    if (!previousItem || previousItem.type !== BLOCK_LIST_ITEM) {
        previousItem = firstSelectedItem;
    }

    if (selectedItems.size > 1) {
        selectedItems.forEach((node) => {
            const index = parentListBlock.getChildIndex(node.key);
            // Can not move first item in the current list
            if (!previousItem || index === 0) {
                return true;
            }
            change = moveAsSubItem(change, node, previousItem.key);
        });
    } else {
        // Only a single top level item in selection
        const firstSelectedItemIndex = parentListBlock.getChildIndex(firstSelectedItem.key);
        if (firstSelectedItemIndex > 0) {
            change = moveAsSubItem(change, firstSelectedItem, previousItem.key);
        }

        // No other items to move in current list check for sublist
        const subListBlock = firstSelectedItem.nodes.find((child) => isList(child)) as Block;
        if (!subListBlock) {
            return change;
        }

        const subItems = subListBlock.nodes as List<Block>;
        change = increaseItemsDepth(change, subItems);
    }

    return change.normalize();
}

/**
 * Move the given item to the sublist at the end of destination item,
 * creating a sublist if needed.
 */
function moveAsSubItem(
    change: Change,
    // The list item to add
    item: Block,
    // The key of the destination node
    destKey: string
): Change {
    const destination = change.value.document.getDescendant<Block>(destKey);
    const lastIndex = destination?.nodes.size;
    const lastChild = destination?.nodes.last();

    // The potential existing last child list
    // @ts-expect-error
    const existingList = isList(lastChild) ? lastChild : null;
    if (existingList) {
        return change.moveNodeByKey(
            item.key,
            existingList.key,
            existingList.nodes.size // as last item
        );
    }

    const currentList = getListForItem(change.value, destination!);
    if (!currentList) {
        throw new Error('Destination is not in a list');
    }

    const newSublist = Block.create({
        object: 'block',
        type: currentList.type,
        data:
            currentList.type === BLOCK_LIST_OL
                ? currentList.data.delete('start')
                : currentList.data,
    });

    // @ts-expect-error
    change.insertNodeByKey(destKey, lastIndex, newSublist, { normalize: false });

    return change.moveNodeByKey(item.key, newSublist.key, 0);
}

/**
 * Split a list item at the start of the current range.
 */
export function splitListItem(change: Change): Change {
    const { value } = change;
    const currentItem = getCurrentItem(value);

    if (!currentItem) {
        return change;
    }

    const splitOffset = value.startOffset;

    // @ts-expect-error
    return change.splitDescendantsByKey(currentItem.key, value.startKey, splitOffset);
}

/**
 * Unwrap items at range from their list.
 */
export function unwrapList(change: Change): Change {
    const items = getItemsAtRange(change.value);
    if (items.isEmpty()) {
        return change;
    }

    // Unwrap the items from their list
    items.forEach((item) => change.unwrapNodeByKey(item.key, { normalize: false }));

    // Parent of the list of the items
    const firstItem = items.first()!;
    const parent = change.value.document.getParent(firstItem.key)!;

    let index = parent.getChildIndex(firstItem.key);

    // Unwrap the items' children
    items.forEach((item) => {
        item.nodes.forEach((node) => {
            change.moveNodeByKey(node.key, parent.key, index, {
                normalize: false,
            });

            index += 1;
        });
    });

    // Finally, remove the now empty items
    items.forEach((item) => change.removeNodeByKey(item.key, { normalize: false }));

    return change;
}

/**
 * Wrap the blocks in the current selection in a new list. Selected
 * lists are merged together.
 */
export function wrapInList(
    change: Change,
    type: string = BLOCK_LIST_UL,
    data: BlockOrderedListData = {}
): Change {
    const selectedBlocks = getHighestSelectedBlocks(change.value);

    // Wrap in container
    // @ts-expect-error
    change.wrapBlock({ type, data: Data.create(data) }, { normalize: false });

    // Wrap in list items
    selectedBlocks.forEach((node) => {
        if (isList(node!)) {
            // Merge its items with the created list
            node.nodes.forEach(({ key }) => change.unwrapNodeByKey(key, { normalize: false }));
        } else {
            change.wrapBlockByKey(node!.key, BLOCK_LIST_ITEM, { normalize: false });
        }
    });

    return change.normalize();
}

/*
 * Returns the highest list of blocks that cover the current selection
 */
function getHighestSelectedBlocks({ document, selection }: Value): List<Block | null> {
    // @ts-expect-error
    const startBlock = document.getClosestBlock(selection.startKey);
    // @ts-expect-error
    const endBlock = document.getClosestBlock(selection.endKey);

    if (startBlock === endBlock) {
        return List.create([startBlock]);
    }

    // @ts-expect-error
    const ancestor = document.getCommonAncestor(startBlock.key, endBlock.key);
    // @ts-expect-error
    const startPath = ancestor.getPath(startBlock.key);
    // @ts-expect-error
    const endPath = ancestor.getPath(endBlock.key);

    // @ts-expect-error
    return ancestor.nodes.slice(startPath[0], endPath[0] + 1);
}
